// Chakra imports
import {
  Box,
  Text,
  SimpleGrid,
  useColorModeValue,
  Icon,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Select,
  useToast,
} from "@chakra-ui/react";
// Custom components
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import React, { useState, useEffect } from "react";
import {
  MdPeople,
  MdAccountCircle,
  MdCheckCircle,
  MdCancel,
  MdAdd,
} from "react-icons/md";
import { formatNumber } from "utils/format";
import usersService from "services/usersService";
import { inviteUser } from "services/authService";
import centersService from "services/centersService";
import { USER_ROLES, ROLE_DISPLAY_NAMES } from "constants/roles";
import UsersTable from "./components/UsersTable";

export default function UsersManagement() {
  // Chakra Color Mode - All hooks must be called at the top level
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const cardBg = useColorModeValue('white', 'navy.800');
  const brandColor = useColorModeValue('brand.500', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const inputBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    byRole: {},
  });
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    total: 0,
  });

  // Invitation modal state
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: USER_ROLES.CENTER_ADMIN,
    center: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [centers, setCenters] = useState([]);
  const [centersLoading, setCentersLoading] = useState(false);
  const toast = useToast();

  // Fetch centers when create modal opens (for center admin dropdown)
  useEffect(() => {
    if (!isCreateOpen) return;
    const fetchCenters = async () => {
      setCentersLoading(true);
      try {
        const result = await centersService.getCenters();
        if (result.success && result.data) {
          setCenters(result.data);
        }
      } catch (err) {
        console.error('Error fetching centers:', err);
      } finally {
        setCentersLoading(false);
      }
    };
    fetchCenters();
  }, [isCreateOpen]);

  // Fetch users data
  useEffect(() => {
    const fetchUsers = async () => {
      setUsersLoading(true);
      try {
        const skip = (pagination.currentPage - 1) * pagination.itemsPerPage;
        const result = await usersService.getUsers({
          skip,
          limit: pagination.itemsPerPage,
        });

        if (result.success && result.data) {
          setUsers(result.data);
          if (result.pagination) {
            setPagination(prev => ({
              ...prev,
              total: result.pagination.total,
            }));
            // Calculate stats from data
            const roles = {};
            let activeCount = 0;
            result.data.forEach(user => {
              if (user.role) {
                roles[user.role] = (roles[user.role] || 0) + 1;
              }
              // Count active users
              if (user.is_active) {
                activeCount++;
              }
            });
            setStats({
              total: result.pagination.total,
              active: activeCount,
              inactive: result.pagination.total - activeCount,
              byRole: roles,
            });
          }
        } else {
          setError(result.error || 'Failed to load users');
        }
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users. Please try again.');
      } finally {
        setUsersLoading(false);
        setLoading(false);
      }
    };

    fetchUsers();
  }, [pagination.currentPage, pagination.itemsPerPage]);

  // Refresh users list
  const refreshUsers = async () => {
    setUsersLoading(true);
    try {
      const skip = (pagination.currentPage - 1) * pagination.itemsPerPage;
      const result = await usersService.getUsers({
        skip,
        limit: pagination.itemsPerPage,
      });

      if (result.success && result.data) {
        setUsers(result.data);
        if (result.pagination) {
          setPagination(prev => ({
            ...prev,
            total: result.pagination.total,
          }));
          // Recalculate stats
          const roles = {};
          let activeCount = 0;
          result.data.forEach(user => {
            if (user.role) {
              roles[user.role] = (roles[user.role] || 0) + 1;
            }
            if (user.is_active) {
              activeCount++;
            }
          });
          setStats({
            total: result.pagination.total,
            active: activeCount,
            inactive: result.pagination.total - activeCount,
            byRole: roles,
          });
        }
      }
    } catch (err) {
      console.error('Error refreshing users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  // Handle invite user
  const handleInviteUser = async () => {
    // Validate form
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    if (!formData.role) {
      errors.role = 'Role is required';
    }
    if (formData.role === USER_ROLES.CENTER_ADMIN && !formData.center.trim()) {
      errors.center = 'Center is required for Center Admin role';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setCreating(true);
    setFormErrors({});

    try {
      const result = await inviteUser({
        name: formData.name.trim(),
        email: formData.email.trim(),
        role: formData.role,
        center: formData.role === USER_ROLES.CENTER_ADMIN ? formData.center.trim() : undefined,
      });

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Invitation sent successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onCreateClose();
        setFormData({
          name: '',
          email: '',
          role: USER_ROLES.CENTER_ADMIN,
          center: '',
        });
        setFormErrors({});
        // Refresh users list
        await refreshUsers();
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to send invitation',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        setFormErrors({ general: result.error || 'Failed to send invitation' });
      }
    } catch (err) {
      console.error('Error sending invitation:', err);
      toast({
        title: 'Error',
        description: 'Failed to send invitation. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setFormErrors({ general: 'Failed to send invitation. Please try again.' });
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
          <Spinner size="xl" color="brand.500" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Alert status="error" borderRadius="15px">
          <AlertIcon />
          <Text color={textColor}>{error}</Text>
        </Alert>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex direction='column' gap='20px'>
        <Flex align='center' justify='space-between' flexWrap='wrap' gap='15px' mb='10px'>
          <Text color={textColor} fontSize='2xl' fontWeight='700'>
            Users Management
          </Text>
          <Button
            leftIcon={<Icon as={MdAdd} />}
            onClick={onCreateOpen}
            colorScheme='blue'
            variant='solid'
            size='md'>
            Send Invite
          </Button>
        </Flex>

        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap='20px' mb='20px'>
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdPeople} color={brandColor} />
                }
              />
            }
            name='Total Users'
            value={formatNumber(stats.total)}
          />
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdCheckCircle} color='green.500' />
                }
              />
            }
            name='Active Users'
            value={formatNumber(stats.active)}
          />
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdAccountCircle} color={brandColor} />
                }
              />
            }
            name='By Role'
            value={formatNumber(Object.keys(stats.byRole || {}).length)}
          />
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdCancel} color='red.500' />
                }
              />
            }
            name='Inactive Users'
            value={formatNumber(stats.inactive)}
          />
        </SimpleGrid>

        {/* Users Table */}
        <Box mt="40px">
          {usersLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minH="200px">
              <Spinner size="lg" color="brand.500" />
            </Box>
          ) : (
            <UsersTable 
              tableData={users} 
              pagination={pagination}
              onPageChange={(page) => setPagination(prev => ({ ...prev, currentPage: page }))}
              onItemsPerPageChange={(itemsPerPage) => setPagination(prev => ({ ...prev, itemsPerPage, currentPage: 1 }))}
            />
          )}
        </Box>
      </Flex>

      {/* Invite User Modal */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="md">
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader color={textColor}>Invite User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap="20px">
              {formErrors.general && (
                <Alert status="error" borderRadius="8px">
                  <AlertIcon />
                  <Text fontSize="sm">{formErrors.general}</Text>
                </Alert>
              )}

              <FormControl isRequired isInvalid={!!formErrors.name}>
                <FormLabel color={textColor}>Full Name</FormLabel>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    if (formErrors.name) setFormErrors({ ...formErrors, name: null });
                  }}
                  placeholder="Enter full name"
                  bg={inputBg}
                  color={textColor}
                />
                {formErrors.name && (
                  <Text color="red.500" fontSize="xs" mt="5px">
                    {formErrors.name}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired isInvalid={!!formErrors.email}>
                <FormLabel color={textColor}>Email</FormLabel>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    if (formErrors.email) setFormErrors({ ...formErrors, email: null });
                  }}
                  placeholder="Enter email address"
                  bg={inputBg}
                  color={textColor}
                />
                {formErrors.email && (
                  <Text color="red.500" fontSize="xs" mt="5px">
                    {formErrors.email}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired isInvalid={!!formErrors.role}>
                <FormLabel color={textColor}>Role</FormLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => {
                    setFormData({ ...formData, role: e.target.value, center: '' });
                    if (formErrors.role) setFormErrors({ ...formErrors, role: null });
                  }}
                  bg={inputBg}
                  color={textColor}
                >
                  <option value={USER_ROLES.ADMIN}>{ROLE_DISPLAY_NAMES[USER_ROLES.ADMIN]}</option>
                  <option value={USER_ROLES.CENTER_ADMIN}>{ROLE_DISPLAY_NAMES[USER_ROLES.CENTER_ADMIN]}</option>
                </Select>
                {formErrors.role && (
                  <Text color="red.500" fontSize="xs" mt="5px">
                    {formErrors.role}
                  </Text>
                )}
              </FormControl>

              {formData.role === USER_ROLES.CENTER_ADMIN && (
                <FormControl isRequired isInvalid={!!formErrors.center}>
                  <FormLabel color={textColor}>Center</FormLabel>
                  <Select
                    value={formData.center}
                    onChange={(e) => {
                      setFormData({ ...formData, center: e.target.value });
                      if (formErrors.center) setFormErrors({ ...formErrors, center: null });
                    }}
                    placeholder={centersLoading ? "Loading centers..." : "Select center"}
                    bg={inputBg}
                    color={textColor}
                    isDisabled={centersLoading}
                  >
                    {centers.map((center) => (
                      <option key={center.id} value={center.name}>
                        {center.full_name || center.name}
                      </option>
                    ))}
                  </Select>
                  {formErrors.center && (
                    <Text color="red.500" fontSize="xs" mt="5px">
                      {formErrors.center}
                    </Text>
                  )}
                </FormControl>
              )}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCreateClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleInviteUser}
              isLoading={creating}
              loadingText="Sending">
              Send Invite
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
